import { Button, LinkButton } from './primitives/Button.js';

import { Card, Container } from './primitives/Misc.js';
import { PWA } from '../data/plans.js';
import { PWA_URL } from '../config.js';
import './Modes.css';

export interface ModesProps {
  onOpenSandbox: () => void;
  onOpenAudit: () => void;
  onGoPricing: () => void;
}

/**
 * The three ways in, in order of commitment: prove it locally, run it once for a
 * project, or leave it running. This is the section the hero's "then keep it
 * that way" promise cashes out in — without it the headline is a claim with
 * nothing behind it.
 *
 * The local card states its own limits rather than burying them, for the reason
 * ADR-041 gives: a ceiling you meet unannounced feels like a bug, and a ceiling
 * you were told about feels like engineering.
 */
export function Modes({ onOpenSandbox, onOpenAudit, onGoPricing }: ModesProps) {
  return (
    <Container id="modes" className="land-section">
      <div className="land-section__head">
        <div>
          <h2 className="land-section__title">Try it locally. Run it once. Or leave it running.</h2>
          <p className="land-section__lede land-section__lede--wide">
            The same engine, three levels of commitment. Most teams prove it on their own data first,
            clean up ahead of a warehouse or AI project, then keep it running once they see how fast
            data drifts back.
          </p>
        </div>
      </div>

      <div className="land-modes__grid">
        <Card className="land-modes__card">
          <div className="land-mono-label">try it · your device</div>
          <h3 className="land-modes__title">Run it on your own data, in your browser</h3>
          <p className="land-modes__body">
            The full match engine compiles into the browser and runs on your machine. Connect
            read-only, profile, match and review entirely on-device — no record content reaches our
            servers at all, so there is nothing to review, approve or sign before you can see what
            your data actually looks like.
          </p>
          <ul className="land-checklist land-modes__list">
            <li>
              <span className="land-checklist__mark">✓</span>The same engine as the cloud, not a
              cut-down demo
            </li>
            <li>
              <span className="land-checklist__mark">✓</span>Records never leave your device
            </li>
            <li>
              {/* ⚠️ **Was "roughly 1–2M records per object type", and that is now a false claim.**
                  That figure is documented assumption A-3, which describes a DuckDB-WASM engine over
                  columnar data. What actually ships scores in TypeScript over in-memory objects and
                  caps at 50,000 by deliberate choice — a run that finishes beats a bigger run that
                  hangs the tab. Promising 1–2M on the page while the product refuses at 50k is
                  exactly the kind of claim `messaging.md §7` exists to prevent, and it only became
                  wrong when the PWA became real. */}
              <span className="land-checklist__mark">✓</span>Up to 50,000 records per file today —
              enough for a mid-market contact export
            </li>
            <li>
              <span className="land-checklist__mark">✓</span>Free, on every plan
            </li>
          </ul>
          <div className="land-modes__limits">
            <div className="land-modes__limits-head">Some things need the cloud, by design</div>
            <p className="land-modes__limits-body">
              {PWA.notByDesign
                .slice(0, 3)
                .map((entry) => entry.limit.toLowerCase())
                .join(', ')}{' '}
              and a few others need a server — a closed laptop cannot watch your data drift.{' '}
              <button type="button" className="land-modes__limits-link" onClick={onGoPricing}>
                See all {PWA.notByDesign.length} and why →
              </button>
            </p>
          </div>
          {/* ⚠️ Opens the PWA. This used to open the audit-request modal — correct while local mode
              did not exist, and wrong the moment it shipped: it asked for an email address to reach
              something that needs no account at all, which is the opposite of what a no-signup test
              drive is for. The lead form is still one click away below. */}
          <LinkButton
            variant="tint"
            size="md"
            href={PWA_URL}
            className="land-modes__cta"
            /* Same tab: this is the product, not a reference. A new tab would leave the marketing
               page behind them as clutter and lose the back button. */
          >
            Open the local sandbox
          </LinkButton>
          <p className="land-modes__cta-note">
            Nothing to install first — it runs in this browser. It can also be installed as an app,
            so it opens from your home screen and keeps working with no connection.{' '}
            <button type="button" className="land-modes__limits-link" onClick={onOpenSandbox}>
              Or tell us what you need →
            </button>
          </p>
        </Card>

        <Card className="land-modes__card">
          <div className="land-mono-label">one-off · project</div>
          <h3 className="land-modes__title">Get ready for a specific project</h3>
          <p className="land-modes__body">
            You have a migration, a warehouse build or an AI rollout with a date on it. Connect your
            systems read-only, get the findings report, approve the cleanup, and hand over one
            version per customer — with a full record of what changed and 30 days to reverse any of
            it.
          </p>
          <ul className="land-checklist land-modes__list">
            <li>
              <span className="land-checklist__mark">✓</span>Findings across every connected system
            </li>
            <li>
              <span className="land-checklist__mark">✓</span>Approve in bulk or record by record
            </li>
            <li>
              <span className="land-checklist__mark">✓</span>Snapshot before every write, reversible
              for 30 days
            </li>
            <li>
              <span className="land-checklist__mark">✓</span>Export golden records with lineage and
              per-field PII tags
            </li>
          </ul>
          <Button variant="primary" size="md" className="land-modes__cta" onClick={onOpenAudit}>
            Run a free data audit
          </Button>
        </Card>

        <Card tone="tint" className="land-modes__card">
          <div className="land-mono-label">ongoing · platform</div>
          <h3 className="land-modes__title">Stay ready as the data moves</h3>
          <p className="land-modes__body">
            Data decays the day after you clean it — reps create records, integrations fire, lists
            get imported. Left running, Dedupr keeps syncing your sources, re-scores quality per
            source and field, tells you when it drifts, and checks every delivery against the rules
            you set, so cleanliness is maintained rather than re-earned.
          </p>
          <ul className="land-checklist land-modes__list">
            <li>
              <span className="land-checklist__mark">✓</span>Scheduled sync, daily or hourly
            </li>
            <li>
              <span className="land-checklist__mark">✓</span>Quality scored per source, object and
              field, tracked over time
            </li>
            <li>
              <span className="land-checklist__mark">✓</span>Alerts when a threshold you set is
              breached
            </li>
            <li>
              <span className="land-checklist__mark">✓</span>Every delivery checked against your own
              rules
            </li>
          </ul>
          <LinkButton variant="tint-outline" size="md" href="#pricing" className="land-modes__cta">
            See plans →
          </LinkButton>
        </Card>
      </div>
    </Container>
  );
}
